import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Button, Input, Card, Badge, Label } from "reactstrap";
import LoadingOverlay from "react-loading-overlay";
import Loader from "react-loaders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faDownload,
  faChevronRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import notify from "../../../../../../Notifications/ToastifyActions";
import FetchStoreOrdersService from "../../../../../../Service/DashboardServices/OrdersServices/FetchStoreOrdersService";
import FetchStoreOrdersSummaryService from "../../../../../../Service/DashboardServices/OrdersServices/FetchStoreOrdersSummaryService";
import RefreshStoreOrdersService from "../../../../../../Service/DashboardServices/OrdersServices/RefreshStoreOrdersService";

const PAGE_SIZE = 15;
const STATUS_LABEL = {
  PENDING: "Pending",
  UNSHIPPED: "Unshipped",
  PARTIALLY_SHIPPED: "Partially shipped",
  SHIPPED: "Shipped",
  CANCELLED: "Cancelled",
};
const EXPORT_COLUMNS = [
  "purchase_date",
  "purchase_time_local",
  "amazon_order_id",
  "order_status",
  "fulfillment",
  "asin",
  "sku",
  "title",
  "quantity_ordered",
  "quantity_shipped",
  "unit_price",
  "item_price",
  "discount",
  "tax",
  "order_total",
  "currency",
  "prime",
  "business",
];

function money(v, cur, fallbackCur) {
  if (v == null) return "–";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: cur || fallbackCur || "USD",
  }).format(v);
}

function fmtDT(iso, tz) {
  if (!iso) return ["–", ""];
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const g = (t) => (p.find((x) => x.type === t) || {}).value;
  return [`${g("month")}-${g("day")}-${g("year")}`, `${g("hour")}:${g("minute")}`];
}

const fmtDay = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${m}-${d}-${y}`;
};

function sellerCentralOrderUrl(orderId, location) {
  const loc = String(location || "US").toUpperCase();
  const host =
    loc === "UK" || loc === "GB"
      ? "https://sellercentral.amazon.co.uk"
      : ["DE", "FR", "IT", "ES", "NL", "SE", "PL", "BE", "TR"].includes(loc)
        ? "https://sellercentral.amazon.de"
        : loc === "JP"
          ? "https://sellercentral.amazon.co.jp"
          : loc === "AU"
            ? "https://sellercentral.amazon.com.au"
            : loc === "IN"
              ? "https://sellercentral.amazon.in"
              : loc === "CA"
                ? "https://sellercentral.amazon.ca"
                : loc === "MX"
                  ? "https://sellercentral.amazon.com.mx"
                  : "https://sellercentral.amazon.com";
  return `${host}/orders-v3/order/${encodeURIComponent(orderId)}`;
}

function StoreOverviewOrders(props) {
  const { profileId, token, location, storeName, onShowInventory } = props;
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [day, setDay] = useState(null);
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [ordering, setOrdering] = useState("-purchase_at");
  const [page, setPage] = useState(1);
  const [openIds, setOpenIds] = useState(() => new Set());
  const [tz, setTz] = useState("America/Los_Angeles");
  const [currency, setCurrency] = useState("USD");
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const loadSeq = useRef(0);
  const qTimer = useRef(null);
  const pageRef = useRef(page);
  pageRef.current = page;

  const load = useCallback(
    async ({ summary: wantSummary = true, page: pageOverride } = {}) => {
      if (!profileId || !token) return;
      const seq = ++loadSeq.current;
      const usePage = pageOverride != null ? pageOverride : pageRef.current;
      setBusy(true);
      setError(null);
      try {
        const params = { start_date: start, end_date: end, day, status, channel, q };
        const [sum, ord] = await Promise.all([
          wantSummary
            ? FetchStoreOrdersSummaryService(token, profileId, params)
            : Promise.resolve(null),
          FetchStoreOrdersService(token, profileId, {
            ...params,
            page: usePage,
            page_size: PAGE_SIZE,
            ordering: "-purchase_at",
          }),
        ]);
        if (seq !== loadSeq.current) return;
        if (sum) {
          setTz(sum.timezone || "America/Los_Angeles");
          setCurrency(sum.currency_code || "USD");
          setStartInput((prev) => prev || sum.start_date || "");
          setEndInput((prev) => prev || sum.end_date || "");
          setSummary(sum);
        }
        if (ord && ord.timezone) setTz(ord.timezone);
        setOrders(ord);
      } catch (e) {
        if (seq !== loadSeq.current) return;
        setError(e.message || "Orders could not be loaded");
        setOrders(null);
      } finally {
        if (seq === loadSeq.current) setBusy(false);
      }
    },
    [profileId, token, start, end, day, status, channel, q]
  );

  useEffect(() => {
    load({ summary: true, page: 1 });
  }, [profileId, start, end, day, status, channel, q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!summary) return;
    load({ summary: false });
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(qTimer.current), []);

  const applyDates = () => {
    if (!startInput || !endInput || startInput > endInput) {
      notify.NotifyError("Choose a start date on or before the end date.");
      return;
    }
    setDay(null);
    setPage(1);
    if (startInput === start && endInput === end) {
      load({ summary: true, page: 1 });
      return;
    }
    setStart(startInput);
    setEnd(endInput);
  };

  const resetFilters = () => {
    setStatus("");
    setChannel("");
    setQ("");
    setQInput("");
    setDay(null);
    setPage(1);
  };

  const toggleOpen = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSort = (key) => {
    setOrdering((prev) => (prev === `-${key}` ? key : `-${key}`));
  };

  const getSortValue = (order, key) => {
    switch (key) {
      case "purchase_at":
        return order.purchase_at || "";
      case "amazon_order_id":
        return (order.amazon_order_id || "").toLowerCase();
      case "order_status":
        return (order.order_status || "").toLowerCase();
      case "fulfillment_channel":
        return (order.fulfillment_channel || "").toLowerCase();
      case "asin":
        return ((order.items && order.items[0] && order.items[0].asin) || "").toLowerCase();
      case "units":
        return order.units == null ? null : Number(order.units);
      case "order_total_amount":
        return order.order_total_amount == null ? null : Number(order.order_total_amount);
      case "is_prime":
        return order.is_prime ? 1 : 0;
      case "ship_to_state":
        return `${order.ship_to_state || ""} ${order.ship_to_country || ""}`.toLowerCase().trim();
      case "last_update_at":
        return order.last_update_at || "";
      default:
        return "";
    }
  };

  const onRefresh = async () => {
    if (refreshing || busy) return;
    setRefreshing(true);
    try {
      await RefreshStoreOrdersService(token, profileId);
      notify.NotifySuccess("Order sync started. New orders show up within a few minutes.");
      await load({ summary: true, page: pageRef.current });
    } catch (e) {
      notify.NotifyError(e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const formatExportDate = (date) =>
    date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : "";

  const onExport = async () => {
    if (!profileId || !token) return;
    setBusy(true);
    try {
      const filter = {
        start_date: start,
        end_date: end,
        day,
        status,
        channel,
        q,
        page_size: 100,
        ordering: "-purchase_at",
      };
      let pageNum = 1;
      let all = [];
      let total = Infinity;
      while (all.length < total) {
        const ord = await FetchStoreOrdersService(token, profileId, { ...filter, page: pageNum });
        total = ord.count || 0;
        const batch = ord.results || [];
        all = all.concat(batch);
        if (!batch.length) break;
        pageNum += 1;
      }
      const rows = [];
      all.forEach((o) => {
        const [purchaseDate, purchaseTime] = fmtDT(o.purchase_at, tz);
        const items = o.items && o.items.length ? o.items : [null];
        items.forEach((i) => {
          rows.push({
            purchase_date: purchaseDate,
            purchase_time_local: purchaseTime,
            amazon_order_id: o.amazon_order_id || "",
            order_status: o.order_status || "",
            fulfillment: o.fulfillment_channel === "AMAZON" ? "FBA" : "FBM",
            asin: (i && i.asin) || "",
            sku: (i && i.sku) || "",
            title: (i && i.title) || "",
            quantity_ordered: i ? i.quantity_ordered : "",
            quantity_shipped: i ? i.quantity_fulfilled : "",
            unit_price: i && i.unit_price != null ? i.unit_price : "",
            item_price: i && i.item_price != null ? i.item_price : "",
            discount: i && i.discount != null ? i.discount : "",
            tax: i && i.tax_total != null ? i.tax_total : "",
            order_total: o.order_total_amount != null ? o.order_total_amount : "",
            currency: o.currency_code || "",
            prime: o.is_prime ? "yes" : "",
            business: o.is_business_order ? "yes" : "",
          });
        });
      });
      const ws = XLSX.utils.json_to_sheet(rows, { header: EXPORT_COLUMNS });
      const csv = XLSX.utils.sheet_to_csv(ws);
      const safeName = (storeName || profileId || "").toString().replace(/[\\/:*?"<>|]/g, "").trim();
      FileSaver.saveAs(
        new Blob([csv], { type: "text/csv;charset=utf-8;" }),
        `Orders_${safeName}_${formatExportDate(new Date())}.csv`
      );
    } catch (e) {
      notify.NotifyError(e.message || "Failed to export orders.");
    } finally {
      setBusy(false);
    }
  };

  const lastUpdatedText = (() => {
    if (!summary || !summary.last_updated_at) return "–";
    const [d, tm] = fmtDT(summary.last_updated_at, tz);
    return `${d} ${tm}`;
  })();

  const sortKey = ordering.replace("-", "");
  const sortDesc = ordering.startsWith("-");
  const sortArrow = (field) =>
    sortKey === field && !sortDesc ? " ▲" : " ▼";

  const days = (summary && summary.daily) || [];
  const maxSales = days.length
    ? Math.max(1, ...days.map((d) => d.sales + d.cancelled_sales))
    : 1;
  const totals = summary && summary.totals;
  const list = (() => {
    const rows = (orders && orders.results) || [];
    if (!rows.length) return rows;
    const sorted = [...rows].sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortDesc ? 1 : -1;
      if (va > vb) return sortDesc ? -1 : 1;
      return 0;
    });
    return sorted;
  })();
  const pageCount = orders ? Math.max(1, Math.ceil(orders.count / orders.page_size)) : 1;
  const from = orders && orders.count ? (orders.page - 1) * orders.page_size + 1 : 0;
  const to = orders ? Math.min(orders.page * orders.page_size, orders.count) : 0;
  const pageNums = [];
  if (orders) {
    for (let p = Math.max(1, orders.page - 2); p <= Math.min(pageCount, orders.page + 2); p++) {
      pageNums.push(p);
    }
  }

  return (
    <Card className="card-of-problemarea store-orders mt-4 p-4">
      <Row className="align-items-center mb-3">
        <Col xs="12" md="auto" className="mb-2 mb-md-0">
          <div className="btn-group btn-group-sm" role="tablist" aria-label="Store data">
            <Button
              type="button"
              size="sm"
              outline
              color="secondary"
              onClick={() => onShowInventory && onShowInventory()}
            >
              Inventory
            </Button>
            <Button type="button" size="sm" className="btn-background-color">
              Orders
            </Button>
          </div>
        </Col>
        <Col className="d-flex justify-content-md-end align-items-center flex-wrap">
          <i className="mr-3 mb-1">
            <b>Orders last updated at {lastUpdatedText}</b>
          </i>
          <Button
            className="btn-background-color mb-1"
            onClick={onRefresh}
            disabled={busy || refreshing}
            title="Refresh"
            style={{ padding: "6px 12px" }}
          >
            <FontAwesomeIcon icon={faSync} spin={refreshing} style={{ fontSize: 14 }} />
          </Button>
          <Button
            className="btn-background-color ml-2 mb-1"
            onClick={onExport}
            disabled={busy || refreshing || !(orders && orders.count)}
            title="Download"
            style={{ padding: "6px 12px" }}
          >
            <FontAwesomeIcon icon={faDownload} style={{ fontSize: 14 }} />
          </Button>
        </Col>
      </Row>

      <Row className="align-items-end mb-3">
        <Col xs="12" lg="auto" className="mr-lg-auto mb-2">
          <h5 className="mb-0">
            <b>Orders</b>
          </h5>
        </Col>
        <Col xs="6" md="auto" className="mb-2" style={{ minWidth: 150 }}>
          <Label className="small text-muted mb-1 d-block">Start date</Label>
          <Input type="date" value={startInput} onChange={(e) => setStartInput(e.target.value)} />
        </Col>
        <Col xs="6" md="auto" className="mb-2" style={{ minWidth: 150 }}>
          <Label className="small text-muted mb-1 d-block">End date</Label>
          <Input type="date" value={endInput} onChange={(e) => setEndInput(e.target.value)} />
        </Col>
        <Col xs="12" md="auto" className="mb-2">
          <Button className="btn-background-color" onClick={applyDates} disabled={busy}>
            Apply dates
          </Button>
        </Col>
        <Col xs="6" md="auto" className="mb-2" style={{ minWidth: 150 }}>
          <Label className="small text-muted mb-1 d-block">Status</Label>
          <Input
            type="select"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="UNSHIPPED">Unshipped</option>
            <option value="PARTIALLY_SHIPPED">Partially shipped</option>
            <option value="SHIPPED">Shipped</option>
            <option value="CANCELLED">Cancelled</option>
          </Input>
        </Col>
        <Col xs="6" md="auto" className="mb-2" style={{ minWidth: 140 }}>
          <Label className="small text-muted mb-1 d-block">Fulfillment</Label>
          <Input
            type="select"
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value);
              setPage(1);
            }}
          >
            <option value="">FBA + FBM</option>
            <option value="AMAZON">FBA</option>
            <option value="MERCHANT">FBM</option>
          </Input>
        </Col>
        <Col xs="12" md="auto" className="mb-2" style={{ minWidth: 220 }}>
          <Label className="small text-muted mb-1 d-block">Search</Label>
          <Input
            type="search"
            placeholder="Order ID, ASIN or SKU"
            value={qInput}
            onChange={(e) => {
              const val = e.target.value;
              setQInput(val);
              clearTimeout(qTimer.current);
              qTimer.current = setTimeout(() => {
                setQ(val.trim());
                setPage(1);
              }, 300);
            }}
          />
        </Col>
      </Row>

      <Card className="border p-3 mb-3" style={{ background: "#fafbf8" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
          <h6 className="mb-0 font-weight-bold">Daily sales</h6>
          {day ? (
            <Badge color="light" className="border">
              Showing {fmtDay(day)}{" "}
              <FontAwesomeIcon
                icon={faTimes}
                className="ml-1"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setDay(null);
                  setPage(1);
                }}
              />
            </Badge>
          ) : null}
          <small className="text-muted">Select a day to see only its orders. Red shows cancelled sales.</small>
        </div>
        <div className="store-orders-daily-bars" role="group" aria-label="Sales per day">
          {days.map((d) => {
            const selected = day === d.date;
            const dimmed = day && !selected;
            return (
              <button
                key={d.date}
                type="button"
                className={`store-orders-daily-bar${selected ? " is-selected" : ""}${dimmed ? " is-dim" : ""}`}
                title={`${fmtDay(d.date)} · ${d.orders} orders · ${money(d.sales, currency)}`}
                aria-pressed={selected}
                onClick={() => {
                  setDay(day === d.date ? null : d.date);
                  setPage(1);
                }}
              >
                <span
                  className="store-orders-daily-bar-sales"
                  style={{ height: `${(d.sales / maxSales) * 100}%` }}
                />
                <span
                  className="store-orders-daily-bar-cancelled"
                  style={{ height: `${(d.cancelled_sales / maxSales) * 100}%` }}
                />
              </button>
            );
          })}
        </div>
        <div className="d-flex justify-content-between small text-muted mt-1">
          <span>{days.length ? fmtDay(days[0].date) : ""}</span>
          <span>{days.length ? fmtDay(days[days.length - 1].date) : ""}</span>
        </div>
      </Card>

      {totals && (
        <div className="store-orders-totals border rounded mb-3 bg-white overflow-hidden">
          {[
            ["Orders", totals.orders.toLocaleString()],
            ["Units", totals.units.toLocaleString()],
            [
              "Sales",
              <>
                {money(totals.sales, currency).replace(/\.\d\d$/, "")}{" "}
                <small className="text-muted">{summary.currency_code || currency}</small>
              </>,
            ],
            [
              "Avg order value",
              totals.avg_order_value == null ? "–" : money(totals.avg_order_value, currency),
            ],
            [
              "Waiting to ship",
              <>
                {totals.waiting_to_ship}{" "}
                <small className="text-muted">{totals.pending} pending</small>
              </>,
            ],
            [
              "Cancelled",
              <>
                {totals.cancelled}{" "}
                <small className="text-muted">{totals.cancelled_rate}%</small>
              </>,
            ],
          ].map(([label, value]) => (
            <div key={label} className="px-3 py-2">
              <div className="small text-muted">{label}</div>
              <div className="font-weight-bold mb-0">{value}</div>
            </div>
          ))}
        </div>
      )}

      <LoadingOverlay
        active={busy || refreshing}
        styles={{
          overlay: (base) => ({ ...base, background: "#f1f4f6", opacity: 1 }),
          content: (base) => ({ ...base, color: "#000" }),
          wrapper: (base) => ({ ...base, minHeight: 220 }),
        }}
        spinner={<Loader active type="ball-pulse" />}
        text={refreshing ? "Refreshing orders..." : "Loading orders..."}
      >
        <div className="table-responsive" style={{ minHeight: 200 }}>
          <table className="table table-sm store-orders-table mb-0" style={{ minWidth: 1180 }}>
            <thead>
              <tr>
                <th style={{ width: 40 }} />
                <th role="button" className="user-select-none" onClick={() => onSort("purchase_at")}>
                  Purchase Date{sortArrow("purchase_at")}
                </th>
                <th role="button" className="user-select-none" onClick={() => onSort("amazon_order_id")}>
                  Order ID{sortArrow("amazon_order_id")}
                </th>
                <th role="button" className="user-select-none" onClick={() => onSort("order_status")}>
                  Status{sortArrow("order_status")}
                </th>
                <th role="button" className="user-select-none" onClick={() => onSort("fulfillment_channel")}>
                  Fulfillment{sortArrow("fulfillment_channel")}
                </th>
                <th role="button" className="user-select-none" onClick={() => onSort("asin")}>
                  Products{sortArrow("asin")}
                </th>
                <th className="text-right user-select-none" role="button" onClick={() => onSort("units")}>
                  Units{sortArrow("units")}
                </th>
                <th className="text-right user-select-none" role="button" onClick={() => onSort("order_total_amount")}>
                  Order Total{sortArrow("order_total_amount")}
                </th>
                <th role="button" className="user-select-none" onClick={() => onSort("is_prime")}>
                  Programs{sortArrow("is_prime")}
                </th>
                <th role="button" className="user-select-none" onClick={() => onSort("ship_to_state")}>
                  Ship To{sortArrow("ship_to_state")}
                </th>
                <th role="button" className="user-select-none" onClick={() => onSort("last_update_at")}>
                  Last Updated{sortArrow("last_update_at")}
                </th>
              </tr>
            </thead>
            <tbody aria-busy={busy ? "true" : undefined}>
              {error ? (
                <tr>
                  <td colSpan={11} className="text-center text-danger py-5">
                    <strong className="d-block">Orders could not be loaded</strong>
                    {error}
                  </td>
                </tr>
              ) : !list.length ? (
                <tr>
                  <td colSpan={11} className="text-center text-muted py-5">
                    <strong className="d-block text-dark">No orders match these filters</strong>
                    Widen the date range or clear the status and search filters.
                    <div className="mt-3">
                      <Button size="sm" className="btn-background-color" onClick={resetFilters}>
                        Clear filters
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                list.map((o) => {
                  const [purchaseDate, purchaseTime] = fmtDT(o.purchase_at, tz);
                  const [updateDate, updateTime] = fmtDT(o.last_update_at, tz);
                  const open = openIds.has(o.amazon_order_id);
                  const first = o.items[0] || {};
                  const more = o.items.length - 1;
                  return (
                    <React.Fragment key={o.amazon_order_id}>
                      <tr className={`store-orders-row${open ? " is-open" : ""}`}>
                        <td>
                          <button
                            type="button"
                            className="store-orders-expand-btn"
                            onClick={() => toggleOpen(o.amazon_order_id)}
                            aria-expanded={open}
                            aria-label={`Show items in order ${o.amazon_order_id}`}
                          >
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className={open ? "store-orders-expand-icon is-open" : "store-orders-expand-icon"}
                            />
                          </button>
                        </td>
                        <td>
                          {purchaseDate}
                          <div className="d-block">{purchaseTime}</div>
                        </td>
                        <td>
                          <a
                            href={sellerCentralOrderUrl(o.amazon_order_id, location)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {o.amazon_order_id}
                          </a>
                        </td>
                        <td>
                          <span className={`store-orders-status store-orders-status-${o.order_status || "PENDING"}`}>
                            {STATUS_LABEL[o.order_status] || o.order_status}
                          </span>
                        </td>
                        <td>{o.fulfillment_channel === "AMAZON" ? "FBA" : "FBM"}</td>
                        <td>
                          {first.asin || "–"}
                          <small className="d-block text-muted">
                            {more > 0 ? `+${more} more product${more > 1 ? "s" : ""}` : first.sku || ""}
                          </small>
                        </td>
                        <td className="text-right">{o.units}</td>
                        <td className="text-right">
                          {o.order_total_amount == null ? (
                            <span className="text-muted" title="Amazon adds amounts once the order leaves Pending">
                              Not priced yet
                            </span>
                          ) : (
                            money(o.order_total_amount, o.currency_code, currency)
                          )}
                        </td>
                        <td>
                          {o.is_prime && (
                            <span className="store-orders-tag store-orders-tag-prime mr-1">Prime</span>
                          )}
                          {o.is_business_order && (
                            <span className="store-orders-tag mr-1">Business</span>
                          )}
                          {o.is_replacement_order && (
                            <span className="store-orders-tag">Replacement</span>
                          )}
                          {!o.is_prime && !o.is_business_order && !o.is_replacement_order && (
                            <span className="text-muted">–</span>
                          )}
                        </td>
                        <td>
                          {o.ship_to_state ? (
                            `${o.ship_to_state}, ${o.ship_to_country}`
                          ) : (
                            <span className="text-muted">–</span>
                          )}
                        </td>
                        <td>
                          {updateDate}
                          <div className="d-block">{updateTime}</div>
                        </td>
                      </tr>
                      {open && (
                        <tr className="store-orders-child-row">
                          <td colSpan={11} className="store-orders-child-cell">
                            <table className="table table-sm mb-0 store-orders-items-table">
                              <thead>
                                <tr>
                                  <th className="store-orders-col-asin">ASIN</th>
                                  <th className="store-orders-col-sku">SKU</th>
                                  <th className="store-orders-col-title">Product Name</th>
                                  <th className="text-right store-orders-col-num">Qty</th>
                                  <th className="text-right store-orders-col-num">Shipped</th>
                                  <th className="text-right store-orders-col-num">Unit Price</th>
                                  <th className="text-right store-orders-col-num">Item Price</th>
                                  <th className="text-right store-orders-col-num">Discount</th>
                                  <th className="text-right store-orders-col-num">Tax</th>
                                  <th className="store-orders-col-promo">Promotion</th>
                                </tr>
                              </thead>
                              <tbody>
                                {o.items.map((item) => (
                                  <tr key={item.order_item_id}>
                                    <td className="store-orders-col-asin text-truncate" title={item.asin || ""}>
                                      {item.asin}
                                    </td>
                                    <td className="store-orders-col-sku text-truncate" title={item.sku || ""}>
                                      {item.sku}
                                    </td>
                                    <td className="store-orders-col-title">
                                      <span
                                        className="store-orders-product-name"
                                        title={item.title || ""}
                                      >
                                        {item.title}
                                      </span>
                                    </td>
                                    <td className="text-right store-orders-col-num">{item.quantity_ordered}</td>
                                    <td className="text-right store-orders-col-num">{item.quantity_fulfilled}</td>
                                    <td className="text-right store-orders-col-num">
                                      {money(item.unit_price, o.currency_code, currency)}
                                    </td>
                                    <td className="text-right store-orders-col-num">
                                      {money(item.item_price, o.currency_code, currency)}
                                    </td>
                                    <td className="text-right store-orders-col-num">
                                      {item.discount == null
                                        ? "–"
                                        : money(item.discount, o.currency_code, currency)}
                                    </td>
                                    <td className="text-right store-orders-col-num">
                                      {money(item.tax_total, o.currency_code, currency)}
                                    </td>
                                    <td className="store-orders-col-promo text-truncate" title={(item.promotion_ids || []).join(", ") || ""}>
                                      {(item.promotion_ids || []).join(", ") || "–"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {orders && (
          <div className="d-flex justify-content-between align-items-center flex-wrap mt-3">
            <span className="text-muted small">
              Showing {from}–{to} of {orders.count} orders
            </span>
            <div className="btn-group btn-group-sm">
              <Button
                size="sm"
                outline
                color="secondary"
                disabled={orders.page === 1}
                onClick={() => setPage(orders.page - 1)}
              >
                ‹
              </Button>
              {pageNums.map((pageNumber) => (
                <Button
                  key={pageNumber}
                  size="sm"
                  className={pageNumber === orders.page ? "btn-background-color" : ""}
                  outline={pageNumber !== orders.page}
                  color="secondary"
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </Button>
              ))}
              <Button
                size="sm"
                outline
                color="secondary"
                disabled={orders.page === pageCount}
                onClick={() => setPage(orders.page + 1)}
              >
                ›
              </Button>
            </div>
          </div>
        )}
      </LoadingOverlay>
    </Card>
  );
}

export default StoreOverviewOrders;
